import type { ReactNode } from 'react';

import { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { LoadingCircle, StsIconName } from '@packages/ui/shared';
import { RadioCard } from './radio-card';
import { RadioCardGroupEmptyState } from './radio-card-group-empty-state';

const CARD_COLUMN_GAP = 16;
const CARD_CONTENT_MIN_WIDTH = 200;
const CARD_GAP = 8;
const DEFAULT_MAX_VISIBLE_CARD_COUNT = 5;
const DEFAULT_MIN_CARD_HEIGHT = 54;
const LOADING_MORE_SPINNER_SIZE = 32;

type RadioCardGroupStyleProps = {
    maxVisibleCardCount: number;
    minCardHeight: number;
};

const useStyles = createUseStyles({
    container: ({ maxVisibleCardCount, minCardHeight }: RadioCardGroupStyleProps) => ({
        maxHeight: toContainerMaxHeight({ maxVisibleCardCount, minCardHeight }),
        overflow: 'auto',
    }),
    endOfList: {
        display: 'flex',
        height: LOADING_MORE_SPINNER_SIZE,
        justifyContent: 'center',
        marginTop: CARD_GAP,
    },
    grid: ({ minCardHeight }: RadioCardGroupStyleProps) => ({
        display: 'grid',
        gridTemplateColumns: `minmax(${CARD_CONTENT_MIN_WIDTH}px, 1fr) auto`,
        gridAutoRows: `minmax(${minCardHeight}px, 1fr)`,
        gap: `${CARD_GAP}px ${CARD_COLUMN_GAP}px`,
    }),
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '210px',
    },
});

export type RadioCardItem = {
    children: ReactNode;
    rightSideContent?: ReactNode;
    value: string;
};

export type RadioCardGroupProps = {
    ariaLabel: string;
    cards: RadioCardItem[];
    isLoading?: boolean;
    name: string;
    onSelect: (value: string) => void;
    selectedValue?: string;
    emptyStateContent?: ReactNode;
    emptyStateIconName?: StsIconName;
    emptyStateTitle?: string;
    maxVisibleCardCount?: number;
    minCardHeight?: number;
    hasMore?: boolean;
    isDisabled?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
};

/**
 * A scrollable group of `RadioCard`s to choose one from.
 *
 * Renders a spinner while a first load is in flight, its own empty state when that load finishes with nothing, and
 * otherwise a card per entry in `cards`. Owning all three means a caller cannot paint an empty state over a fetch that
 * has not come back yet. A reload with cards already on screen dims them rather than replacing them, so the list does
 * not blank out between refreshes. When given `onLoadMore`, a reload also scrolls the list back to the top;
 * `isDisabled` dims the cards without scrolling. While `hasMore`, it calls `onLoadMore` once the end of the list is
 * within half a box height of the visible area, and shows a spinner there while `isLoadingMore`.
 *
 * @param props - The props for the `RadioCardGroup` component.
 * @returns The rendered `RadioCardGroup` component.
 */
export function RadioCardGroup({
    ariaLabel,
    cards,
    isLoading,
    name,
    onSelect,
    selectedValue,
    emptyStateContent,
    emptyStateIconName = 'x-circle',
    emptyStateTitle = 'No results',
    maxVisibleCardCount = DEFAULT_MAX_VISIBLE_CARD_COUNT,
    minCardHeight = DEFAULT_MIN_CARD_HEIGHT,
    hasMore,
    isDisabled,
    isLoadingMore,
    onLoadMore,
}: RadioCardGroupProps) {
    const classes = useStyles({ maxVisibleCardCount, minCardHeight });
    const containerRef = useRef<HTMLDivElement | null>(null);
    const endOfListRef = useRef<HTMLDivElement | null>(null);

    const hasCards = cards.length > 0;

    const isPaged = Boolean(onLoadMore);
    const shouldObserveEndOfList = isPaged && hasMore && !isDisabled && !isLoading && !isLoadingMore;
    const rootMargin = `0px 0px ${toContainerMaxHeight({ maxVisibleCardCount, minCardHeight }) / 2}px 0px`;

    /** Scrolls a paged list back to the top when it reloads, so it starts from the first card, as on a first load. */
    useEffect(() => {
        if (isPaged && isLoading && containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    }, [isLoading, isPaged]);

    /**
     * Observes the end of the list and calls `onLoadMore` once it's within half a box height of the visible area, so
     * the next page starts loading a couple of cards before the end comes into view. Observing starts again after
     * each page, so pages keep loading while the end is still that close.
     */
    useEffect(() => {
        const endOfList = endOfListRef.current;

        if (!shouldObserveEndOfList || !endOfList) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    onLoadMore?.();
                }
            },
            { root: containerRef.current, rootMargin }
        );

        observer.observe(endOfList);

        return () => observer.disconnect();
    }, [onLoadMore, rootMargin, shouldObserveEndOfList]);

    if (!hasCards && isLoading) {
        return (
            <div aria-busy aria-label={ariaLabel} className={classes.loading} role="status">
                <LoadingCircle size={64} />
            </div>
        );
    }

    if (!hasCards) {
        return (
            <RadioCardGroupEmptyState ariaLabel={ariaLabel} iconName={emptyStateIconName} title={emptyStateTitle}>
                {emptyStateContent}
            </RadioCardGroupEmptyState>
        );
    }

    return (
        <div className={classes.container} ref={containerRef}>
            <div aria-label={ariaLabel} className={classes.grid} role="radiogroup">
                {cards.map((card) => (
                    <RadioCard
                        key={card.value}
                        isCardDisabled={isDisabled || isLoading}
                        isSelected={card.value === selectedValue}
                        name={name}
                        onSelect={onSelect}
                        rightSideContent={card.rightSideContent}
                        value={card.value}
                    >
                        {card.children}
                    </RadioCard>
                ))}
            </div>

            {hasMore && (
                <div className={classes.endOfList} ref={endOfListRef}>
                    {isLoadingMore && (
                        <div aria-busy aria-label={ariaLabel} role="status">
                            <LoadingCircle size={LOADING_MORE_SPINNER_SIZE} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function toContainerMaxHeight({ maxVisibleCardCount, minCardHeight }: RadioCardGroupStyleProps) {
    return minCardHeight * maxVisibleCardCount + CARD_GAP * (maxVisibleCardCount - 1);
}
